/** REAL promotion tracking; loaded only by enabled feature sections. */
export const initNoStockTracking = (() => {
  const sectionSelector = '[data-real-promo-section]';
  const promoSelector = '[data-real-promo-id]';
  const views = new Map();
  const seen = new Set();
  let syncNoStockConsent;
  const warned = new Set();
  let ownTagId = '';
  let loadingPrivacy = false;
  let explicitDenial = false;

  function warn(message) {
    if (warned.has(message)) return;
    warned.add(message);
    console.warn('[REAL promotions]', message);
  }

  function allowed() {
    try {
      return !explicitDenial && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  }

  // Keep the source collection and pagination, without search terms, tracking
  // IDs, preview tokens, arbitrary query values, or fragments.
  function reportURL(value) {
    try {
      const url = new URL(value, window.location.href);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      const page = url.searchParams.get('page');
      url.search = '';
      url.hash = '';
      if (page && /^\d+$/.test(page)) url.searchParams.set('page', page);
      return url.href;
    } catch {
      return '';
    }
  }

  function command() {
    window.dataLayer.push(arguments);
  }

  function consentState() {
    return {
      analytics_storage: allowed() ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    };
  }

  function googleSender(id) {
    if (!/^G-[A-Z0-9]+$/.test(id)) {
      warn('Set a valid GA4 measurement ID in Theme settings > REAL: Promotion analytics.');
      return null;
    }
    if (ownTagId) return ownTagId === id ? command : null;

    // A Google tag configured under AW- or GT- can already route to GA4.
    // The public data layer is not a destination registry. Reuse the sender;
    // send() addresses only the chosen GA4 destination with send_to. Its owner
    // retains all configuration and consent handling.
    if (typeof window.gtag === 'function') return window.gtag;
    if (document.querySelector('script[src*="googletagmanager.com/"]')) {
      warn('An existing Google tag manager needs review before enabling the promotion sender.');
      return null;
    }
    if (window.dataLayer && !Array.isArray(window.dataLayer)) return null;

    window.dataLayer = window.dataLayer || [];
    ownTagId = id;
    window.gtag = command;
    command('consent', 'default', consentState());
    command('js', new Date());
    command('config', id, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    script.addEventListener('error', () => warn('Google Analytics could not load; promotion events may be blocked.'));
    document.head.append(script);
    return command;
  }

  function send(name, promo, extra = {}) {
    try {
      const section = promo?.closest(sectionSelector);
      const mode = section?.dataset.realPromoMode;
      if (!section || !['console', 'ga4'].includes(mode) || !allowed()) return false;
      if (!promo.dataset.realPromoId || !section.dataset.realPromoPlacement) return false;
      if (section.hidden || promo.closest('real-campaign-window')?.hidden) return false;
      const parameters = {
        promo_id: promo.dataset.realPromoId,
        promo_name: (promo.dataset.realPromoName || promo.dataset.realPromoId).slice(0, 100),
        promo_placement: section.dataset.realPromoPlacement,
        page_location: reportURL(window.location.href),
        ...extra,
      };
      if (mode === 'console') {
        console.info('[REAL promotions]', name, parameters);
        return true;
      }
      if (section.dataset.realPromoEditor === 'true' || window.Shopify?.designMode) return false;
      const sender = googleSender(section.dataset.realPromoMeasurementId || '');
      if (!sender) return false;
      if (section.dataset.realPromoDebug === 'true') parameters.debug_mode = true;
      sender('event', name, { ...parameters, send_to: section.dataset.realPromoMeasurementId });
      return true;
    } catch {
      // Analytics must never interrupt native links or disclosure controls.
      return false;
    }
  }

  function click(event) {
    if (event.defaultPrevented || (event.type === 'auxclick' ? event.button !== 1 : event.button > 0)) return;
    const copyButton = event.target?.closest?.('[data-real-promo-click="copy_code"]');
    if (copyButton && event.type === 'click') {
      send('real_promo_click', copyButton.closest(promoSelector), { promo_action: 'copy_code' });
      return;
    }
    const link = event.target?.closest?.('a[href]');
    const promo = link?.closest(promoSelector);
    if (!promo || !link.getAttribute('href')?.trim() || link.getAttribute('href').startsWith('#')) return;
    const destination = reportURL(link.href);
    if (destination) send('real_promo_click', promo, { link_url: destination });
  }


  function cancel(state) {
    clearTimeout(state.timer);
    state.timer = null;
  }

  function viewKey(promo) {
    return JSON.stringify([
      reportURL(window.location.href),
      promo.dataset.realPromoId,
      promo.closest(sectionSelector)?.dataset.realPromoPlacement,
    ]);
  }

  function schedule(promo, state) {
    if (!allowed() || document.visibilityState !== 'visible' || !state.visible || seen.has(viewKey(promo))) {
      cancel(state);
      return;
    }
    if (state.timer !== null) return;
    const startedKey = viewKey(promo);
    state.timer = setTimeout(() => {
      state.timer = null;
      if (!promo.isConnected || !state.visible || document.visibilityState !== 'visible') return;
      const key = viewKey(promo);
      if (key !== startedKey) return schedule(promo, state);
      if (!seen.has(key) && send('real_promo_view', promo)) seen.add(key);
    }, 1000);
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const state = views.get(entry.target);
      if (!state) continue;
      // A tall below-grid panel can exceed the viewport. Require half of the
      // smaller area, so such a promotion can still earn an impression.
      const rect = entry.boundingClientRect;
      const root = entry.rootBounds;
      const area = Math.min(rect.width, root?.width || rect.width) * Math.min(rect.height, root?.height || rect.height);
      state.visible = entry.isIntersecting && area > 0 &&
        entry.intersectionRect.width * entry.intersectionRect.height >= area * 0.5;
      schedule(entry.target, state);
    }
  }, { threshold: Array.from({ length: 101 }, (_, i) => i / 100) }) : null;

  function discover() {
    for (const [promo, state] of views) {
      if (promo.isConnected) continue;
      cancel(state);
      observer?.unobserve(promo);
      views.delete(promo);
    }
    if (!observer) return;
    document.querySelectorAll(`${sectionSelector} [data-real-promo-view]`).forEach((promo) => {
      if (views.has(promo)) return;
      views.set(promo, { visible: false, timer: null });
      observer.observe(promo);
    });
  }

  function campaignChanged(event) {
    discover();
    for (const [promo, state] of views) {
      if (!event.target.contains(promo)) continue;
      cancel(state);
      state.visible = false;
      observer?.unobserve(promo);
      if (!event.target.hidden) observer?.observe(promo);
    }
  }

  function syncConsent(event) {
    if (typeof event?.detail?.analyticsAllowed === 'boolean') explicitDenial = !event.detail.analyticsAllowed;
    if (ownTagId) command('consent', 'update', consentState());
    syncNoStockConsent?.();
    for (const [promo, state] of views) schedule(promo, state);
  }

  function loadPrivacy() {
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed) return syncConsent();
    if (loadingPrivacy || !window.Shopify?.loadFeatures) return;
    loadingPrivacy = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        loadingPrivacy = false;
        syncConsent();
      });
    } catch {
      loadingPrivacy = false;
    }
  }

  document.addEventListener('click', click);
  document.addEventListener('auxclick', click);
  document.addEventListener('real:promotion-open', (event) => send('real_promo_open', event.target?.closest?.(promoSelector)));
  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('shopify:section:load', discover);
  document.addEventListener('real:campaign-change', campaignChanged);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(discover));
  document.addEventListener('visibilitychange', () => {
    for (const [promo, state] of views) {
      cancel(state);
      state.visible = false;
      observer?.unobserve(promo);
      if (document.visibilityState === 'visible') observer?.observe(promo);
    }
  });
  window.addEventListener('load', loadPrivacy, { once: true });
  window.addEventListener('pageshow', () => { discover(); loadPrivacy(); });
  window.addEventListener('pagehide', () => { for (const state of views.values()) cancel(state); });
  discover();
  loadPrivacy();

  // Liquid invokes this only on wholly unavailable product pages. Promotion-only
  // pages do not scan for sold-out panels or register their lifecycle handlers.
  return function initNoStockTracking() {
    if (syncNoStockConsent) return;
    const seenNoStock = new Set();
    function noStockViews() {
      if (!allowed() || document.visibilityState !== 'visible') return;
      document.querySelectorAll('[data-real-nostock-product-id]').forEach((panel) => {
        try {
          const mode = panel.dataset.realPromoMode;
          const id = panel.dataset.realNostockProductId;
          if (!panel.isConnected || panel.hidden || !/^\d+$/.test(id || '') || !['console', 'ga4'].includes(mode)) return;
          const page = reportURL(window.location.href);
          if (!page) return;
          const key = JSON.stringify([page, id]);
          if (seenNoStock.has(key)) return;
          const parameters = {
            page_location: page,
            product_id: id,
            product_type: (panel.dataset.realNostockProductType || '').slice(0, 100),
            stock_status: 'unavailable',
            tracking_version: 'horizon_product_v1',
          };
          if (mode === 'console') {
            console.info('[REAL promotions]', 'view_item_nostock_custom', parameters);
          } else {
            if (panel.dataset.realPromoEditor === 'true' || window.Shopify?.designMode) return;
            const sender = googleSender(panel.dataset.realPromoMeasurementId || '');
            if (!sender) return;
            if (panel.dataset.realPromoDebug === 'true') parameters.debug_mode = true;
            sender('event', 'view_item_nostock_custom', { ...parameters, send_to: panel.dataset.realPromoMeasurementId });
          }
          seenNoStock.add(key);
        } catch {
          // Tracking must not interrupt sold-out recovery or product browsing.
        }
      });
    }

    syncNoStockConsent = noStockViews;
    document.addEventListener('visibilitychange', noStockViews);
    document.addEventListener('shopify:section:load', noStockViews);
    document.addEventListener('shopify:section:unload', () => queueMicrotask(noStockViews));
    window.addEventListener('pageshow', noStockViews);
    noStockViews();
    loadPrivacy();
  };
})();
